// A friend's face for the friends panel (owner 2026-10-01), the login circles and the licence: their
// own character in their own outfit — the front-facing standing pose of the body with each worn
// garment on top (the walk sheets share one grid, scripts/anim/build_walk_sheets.py), cropped to head
// and shoulders. Cached per look; resolves null if nothing can be drawn.
//
// Drawn from each sheet's small portrait part (scripts/anim/build_portrait_parts.py), as plain
// images, never the sheets themselves: a sheet is ~30 MB decoded, and loading every sheet each
// friend wears ran an iPhone out of memory when the friends list opened (owner 2026-10-18).
// In a look (character look, 2026-10-29) each part is recoloured from its mask before it is stacked. A style whose
// hair parts do not load draws the default style's (hairArt loadHair); when not even those do there is no portrait,
// and nothing is cached, so the next open asks again.
import { CROP, PART, fitsPart } from './portraitCrop.mjs';
import { assetUrl } from '../assetVersion.js';
import { lookUniforms } from '../look/lookPalette.mjs';
import { MASKED } from '../look/lookMasks.mjs';
import { portraitCacheKey, portraitKeys, portraitLifts, recolourImageData } from '../look/portraitLook.mjs';
import { hairFallback, hairKeys, loadHair } from '../look/hairArt.mjs';

export { CROP };
const cache = new Map();
const BODY = 1;                                  // portraitKeys' index of the body part

const loadImage = (url) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);            // a garment without a sheet (or a part without a mask): skipped
  img.src = url;
});
const loadPart = (key) => loadImage(assetUrl(`/assets/anim/portrait/${key}.webp`));
const loadMask = (key) => (MASKED.has(`portrait/${key}`) ? loadImage(assetUrl(`/assets/anim/portrait/${key}_mask.webp`)) : null);

const pixels = (img) => {
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(img, 0, 0);
  return { c, g, data: g.getImageData(0, 0, c.width, c.height) };
};

// The part in the look: a canvas the same size as the part image.
function recoloured(img, mask, uniforms) {
  const part = pixels(img), m = pixels(mask);
  recolourImageData(part.data.data, img.width, img.height, m.data.data, mask.width, uniforms);
  part.g.putImageData(part.data, 0, 0);
  m.c.width = m.c.height = 0;
  return part.c;
}

// `scene` is no longer needed (kept so the callers stay as they are).
export function friendPortrait(_scene, friend, crop = CROP) {
  const character = friend?.character ?? 'natalka';
  const look = friend?.outfit?.look ?? null;
  const equipped = friend?.outfit?.equipped ?? null;
  const hair = hairKeys('portrait', character, look, equipped);
  const uniforms = lookUniforms(character, look);
  const cacheKey = portraitCacheKey(portraitKeys(character, equipped, look, hair), crop, character, look);
  if (cache.has(cacheKey)) return cache.get(cacheKey);
  // every part is asked for at once, each once; the hair's are waited for through loadHair
  const asked = new Map(), images = new Map();
  const part = (key) => { if (!asked.has(key)) asked.set(key, loadPart(key).then((img) => { images.set(key, img); return img; })); return asked.get(key); };
  portraitKeys(character, equipped, look, hair).forEach(part);
  const load = (keys, done) => { Promise.all(keys.map(part)).then(done); };
  const has = (key) => !!images.get(key);
  let lifts = [];                                // px each part draws higher (the hat on a style, as walking)
  const p = new Promise((resolve) => loadHair(load, has, hair, hairFallback(character), resolve)).then((drawn) => {
    if (!drawn) return null;
    const keys = portraitKeys(character, equipped, look, drawn);
    lifts = portraitLifts(keys, character, equipped, look, drawn);
    const masks = uniforms ? Promise.all(keys.map(loadMask)) : Promise.resolve([]);
    return Promise.all([Promise.all(keys.map(part)), masks]);
  }).then((loaded) => {
    if (!loaded) return null;
    const [parts, partMasks] = loaded;
    if (!parts[BODY] || !fitsPart(crop)) return null;
    try {
      const c = document.createElement('canvas');
      c.width = crop.w; c.height = crop.h;
      const g = c.getContext('2d');
      parts.forEach((img, i) => {
        if (!img) return;
        const src = partMasks[i] ? recoloured(img, partMasks[i], uniforms) : img;
        g.drawImage(src, crop.x - PART.x, crop.y - PART.y, crop.w, crop.h, 0, -lifts[i], crop.w, crop.h);
        if (src !== img) src.width = src.height = 0;
      });
      const url = c.toDataURL('image/png');
      c.width = c.height = 0;                   // let Safari drop the canvas memory now
      return url;
    } catch { return null; }
  });
  cache.set(cacheKey, p);
  p.then((url) => { if (!url) cache.delete(cacheKey); });
  return p;
}
